import { useMemo } from "react";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import TableSkeleton from "Components/Common/Skeleton/TableSkeleton";
import Table, { type Column } from "Components/Table/Table";
import {
  useProjectAiaSummary,
  type MoneyValue,
  type AiaG703LineItem,
} from "../../../../hooks/useProjectAiaSummary";

interface Props {
  projectId: string;
}

interface G702Line {
  num: string;
  label: string;
  val: string;
  indent?: boolean;
  cls?: string;
  total?: boolean;
}

interface Totals {
  sched: MoneyValue;
  prev: MoneyValue;
  curr: MoneyValue;
  stored: MoneyValue;
  total: MoneyValue;
  remain: MoneyValue;
  retainage: MoneyValue;
  pct: number;
}

const amountOf = (money: MoneyValue | undefined): number =>
  Number(money?.amount ?? 0);

const formatMoney = (money: MoneyValue | undefined | null): string => {
  if (!money || money.amount == null) return "—";
  const code = money.currency?.code || "USD";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(money.amount ?? 0));
};

const formatPercent = (value: number | string | undefined): string => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "0%";
  return `${Number.isInteger(num) ? num : num.toFixed(2)}%`;
};

const getPctClass = (pct: number | undefined): string => {
  const n = Number(pct ?? 0);
  if (n === 100) return "pct-high";
  if (n === 0) return "pct-zero";
  if (n < 30) return "pct-warn";
  return "";
};

const G702_SKELETON_ROWS = Array.from({ length: 11 }, (_, i) => i);

const G703_COLUMNS: Column<AiaG703LineItem>[] = [
  {
    header: "Item",
    render: (row) => row.aiaItem?.itemLabel ?? row.aiaItemCode ?? "—",
  },
  {
    header: "Description of Work",
    render: (row) => row.description ?? row.aiaItem?.itemName ?? "—",
  },
  { header: "Scheduled Value", render: (row) => formatMoney(row.sovValue) },
  { header: "Prev. Billed", render: (row) => formatMoney(row.previousBilled) },
  {
    header: "This Period",
    render: (row) => formatMoney(row.currentApplication),
  },
  {
    header: "Stored Materials",
    render: (row) => formatMoney(row.currentStoredMaterial),
  },
  {
    header: "Total Completed",
    render: (row) => formatMoney(row.totalCompletedAndStored),
  },
  {
    header: "% Complete",
    render: (row) => (
      <span className={getPctClass(row.percentageComplete)}>
        {formatPercent(row.percentageComplete)}
      </span>
    ),
  },
  {
    header: "Balance to Finish",
    render: (row) => formatMoney(row.balanceToFinish),
  },
  { header: "Retainage", render: (row) => formatMoney(row.retainageAmount) },
];

export default function AIASection({ projectId }: Props) {
  const { summary, loading, error } = useProjectAiaSummary(projectId, "admin");

  const g702 = summary?.g702;
  const g703Rows = useMemo<AiaG703LineItem[]>(
    () => summary?.g703?.lineItems ?? [],
    [summary],
  );
  const retainageRate =
    summary?.g703?.retainageRate || g702?.retainageRate || "";

  const g702Lines = useMemo<G702Line[]>(() => {
    if (!g702) return [];
    return [
      {
        num: "1",
        label: "Original contract sum",
        val: formatMoney(g702.originalContractSum),
      },
      // { num: "1a", label: "SOV update", val: formatMoney(g702.sovUpdate) },
      {
        num: "2",
        label: "Net change by change orders",
        val: formatMoney(g702.netChangeByChangeOrders),
      },
      {
        num: "3",
        label: "Contract sum to date",
        val: formatMoney(g702.contractSumToDate),
      },
      {
        num: "4",
        label: "Total completed & stored to date",
        val: formatMoney(g702.totalCompletedAndStored),
      },
      {
        num: "5a",
        label: `Retainage: ${formatPercent(g702.retainageRate)} of completed work`,
        val: formatMoney(g702.completedWorkRetainage),
        indent: true,
      },
      {
        num: "5b",
        label: "Retainage: stored material",
        val: formatMoney(g702.storedAmountRetainage),
        indent: true,
      },
      {
        num: "5c",
        label: "Total retainage",
        val: formatMoney(g702.totalRetainage),
      },
      {
        num: "6",
        label: "Total earned less retainage",
        val: formatMoney(g702.totalEarnedLessRetainage),
        cls: "blue",
      },
      {
        num: "7",
        label: "Less previous certificates for payment",
        val: formatMoney(g702.lessPreviousCertificates),
      },
      {
        num: "8",
        label: "Current payment due",
        val: formatMoney(g702.currentPaymentDue),
        cls: "green",
        total: true,
      },
      {
        num: "9",
        label: "Balance to finish, including retainage",
        val: formatMoney(g702.balanceToFinish),
      },
    ];
  }, [g702]);

  const totals = useMemo<Totals>(() => {
    const acc = g703Rows.reduce(
      (a, row) => ({
        sched: a.sched + amountOf(row.sovValue),
        prev: a.prev + amountOf(row.previousBilled),
        curr: a.curr + amountOf(row.currentApplication),
        stored: a.stored + amountOf(row.currentStoredMaterial),
        total: a.total + amountOf(row.totalCompletedAndStored),
        remain: a.remain + amountOf(row.balanceToFinish),
        retainage: a.retainage + amountOf(row.retainageAmount),
      }),
      {
        sched: 0,
        prev: 0,
        curr: 0,
        stored: 0,
        total: 0,
        remain: 0,
        retainage: 0,
      },
    );

    const currency =
      g703Rows[0]?.sovValue?.currency ?? g702?.contractSumToDate?.currency;
    const pct = acc.sched > 0 ? (acc.total / acc.sched) * 100 : 0;

    const wrap = (amount: number): MoneyValue => ({ amount, currency });

    return {
      sched: wrap(acc.sched),
      prev: wrap(acc.prev),
      curr: wrap(acc.curr),
      stored: wrap(acc.stored),
      total: wrap(acc.total),
      remain: wrap(acc.remain),
      retainage: wrap(acc.retainage),
      pct,
    };
  }, [g702, g703Rows]);

  const g703FooterRow = useMemo<React.ReactNode[]>(
    () => [
      "TOTAL",
      null,
      formatMoney(totals.sched),
      formatMoney(totals.prev),
      formatMoney(totals.curr),
      formatMoney(totals.stored),
      formatMoney(totals.total),
      <span className={getPctClass(totals.pct)}>
        {formatPercent(totals.pct)}
      </span>,
      formatMoney(totals.remain),
      formatMoney(totals.retainage),
    ],
    [totals],
  );

  return (
    <div>
      <h1 className="sectionTitle">AIA Summary</h1>
      <div className="section-sub">
        AIA G702 Application &amp; Certificate for Payment · G703 Continuation
        Sheet
      </div>

      {/* ── G702 ─────────────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-title">
          G702 — Application &amp; Certificate for Payment
          {retainageRate ? (
            <span className="cardSubTitle">
              Retainage rate: {formatPercent(retainageRate)}
            </span>
          ) : null}
        </div>

        {loading ? (
          G702_SKELETON_ROWS.map((i) => (
            <div className="aia-line" key={`g702-sk-${i}`}>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  maxWidth: 360,
                }}
              >
                <Skeleton width={18} height={14} />
                <Skeleton
                  width={i === 7 ? "58%" : i % 2 === 0 ? "70%" : "52%"}
                  height={14}
                />
              </span>
              <Skeleton
                width={i === 9 ? 120 : 96}
                height={16}
                style={{ flexShrink: 0 }}
              />
            </div>
          ))
        ) : error ? (
          <div className="badge b-review">
            <span className="fw-bold">Unable to load:</span>{" "}
            <span className="fw-normal">{error}</span>
          </div>
        ) : g702Lines.length ? (
          g702Lines.map((line) => (
            <div
              className={`aia-line${line.total ? " total" : ""}`}
              key={line.num}
            >
              <span>
                <span className="aia-lnum">{line.num}</span>
                <span
                  className={`aia-label${line.indent ? " indent" : ""}`}
                  style={line.total ? { fontWeight: 600 } : {}}
                >
                  {line.label}
                </span>
              </span>
              <span className={`aia-val${line.cls ? ` ${line.cls}` : ""}`}>
                {line.val}
              </span>
            </div>
          ))
        ) : (
          <div className="aia-line">
            <span className="aia-label">No G702 data available.</span>
          </div>
        )}
      </div>

      {/* ── G703 ─────────────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-title">G703 — Schedule of Values</div>
        {loading ? (
          <TableSkeleton cols={10} rows={6} />
        ) : error ? (
          <div className="badge b-review">
            <span className="fw-bold">Unable to load:</span>{" "}
            <span className="fw-normal">{error}</span>
          </div>
        ) : (
          <div className="g703-wrap">
            <Table
              data={g703Rows}
              columns={G703_COLUMNS}
              footerRow={g703Rows.length > 0 ? g703FooterRow : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
}
